import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_294: DocMigration = {
  maxSchemaVersion: "0.0.294",
  description:
    "Electrical Rules into table format and add default electrical rules as setting set to allow for removing default rules from runtime.",
  apply(json: any): any {
    const settings = json.settings;
    for (const settingId in settings) {
      const setting = settings[settingId];
      if (setting.type !== "ELECTRICAL_SETTINGS") {
        continue;
      }
      const rulesAsTable: { [id: string]: any } = {};

      for (const ruleIndex in setting.rules) {
        const rule = setting.rules[ruleIndex];
        rulesAsTable[rule.id] = { ...rule, id: rule.id };
      }

      setting.rules = rulesAsTable;
      json.settings[settingId] = setting;
    }

    // create settings with default electrical rules
    // shift all sortIndices up by 1
    for (const settingsSetId in json.settingsSets) {
      json.settingsSets[settingsSetId].sortIndex++;
    }

    const newSettingsSetId = uid();
    json.settingsSets[newSettingsSetId] = {
      type: "GLOBAL",
      entityType: "SETTINGS_SET",
      name: "Electrical Default Rules",
      id: newSettingsSetId,
      sortIndex: 0,
    };

    // create settings set for electrical rules at beginning of settings sets
    const newSettingsId = uid();
    json.settings[newSettingsId] = {
      entityType: "SETTINGS",
      type: "ELECTRICAL_SETTINGS",
      id: newSettingsId,
      parentId: newSettingsSetId,
      name: "Electrical Settings",
      rules: convertRuleArrayToRulesTable(defaultElectricalLayoutRules),
      mergeRules: false,
      sortIndex: undefined,
    };

    return json;
  },
};

export default migration_0_0_294;

function convertRuleArrayToRulesTable(rules: any[]) {
  const table = {};
  rules.forEach((rule: any) => {
    table[rule.id] = rule;
  });
  return table;
}

const defaultElectricalLayoutRules: any[] = [
  {
    id: "B6hDNutsUZouooWs",
    filters: {
      spaceType: [{ spaceType: "BEDROOM" }],
      area: [{ minimum: 100 }],
    },
    electricalLayout: {
      layoutType: "STANDARD_OUTLETS",
      singleOutletMax: 144,
      minWallLength: 24,
      outletSpacing: 72,
    },
  },
  {
    id: "IoBQytZYWWsA7x6r",
    filters: {
      spaceType: [{ spaceType: "DINING" }],
      area: [{ minimum: 100 }],
    },
    electricalLayout: {
      layoutType: "STANDARD_OUTLETS",
    },
  },
  {
    id: "eeNEZmZ3XVt6uCjv",
    filters: {
      spaceType: [{ spaceType: "LIVING" }],
      area: [{ minimum: 100 }],
    },
    electricalLayout: {
      layoutType: "STANDARD_OUTLETS",
    },
  },
  {
    id: "PNwZRsdKfAujTHDJ",
    filters: {
      spaceType: [{ spaceType: "CIRCULATION" }],
      area: [{ minimum: 100 }],
    },
    electricalLayout: {
      layoutType: "STANDARD_OUTLETS",
    },
  },
  {
    id: "EBQIzNqW1VllQp5n",
    filters: {
      roomType: [{ roomType: "GARAGE" }],
      area: [{ minimum: 300 }],
    },
    electricalLayout: {
      layoutType: "STANDARD_OUTLETS",
      loadType: "OUTLET_GFCI",
      singleOutletMax: 300,
    },
  },
  {
    id: "MmNfMCyHIlnE4xam",
    filters: {
      spaceType: [{ spaceType: "TRANSITION" }],
      area: [{ minimum: 100 }],
    },
    electricalLayout: {
      layoutType: "STANDARD_OUTLETS",
      singleOutletMax: 240,
      minWallLength: 72,
    },
  },
  {
    id: "lzOKjtNciJeC9SRq",
    filters: {
      spaceType: [{ spaceType: "EXTERIOR_COVERED" }],
      roomType: [
        { roomType: "FRONT_PORCH", negate: true },
        { roomType: "SOLARIUM", negate: true },
      ],
    },
    electricalLayout: {
      layoutType: "SINGLE_LIGHT",
      controllerPlacementType: [{ type: "PRIMARY_ENTRANCE_OUTSIDE_ROOMSET" }],
    },
  },
  {
    id: "2R6VOzUuSy2UlyO4",
    filters: {
      roomType: [{ roomType: "MASTER_BED" }],
    },
    electricalLayout: {
      layoutType: "FOUR_LIGHTS",
      controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
      addFan: true,
    },
  },
  {
    id: "2S8WecGNSsS3a3qL",
    filters: {
      roomType: [{ roomType: "PATIO" }],
    },
    electricalLayout: {
      layoutType: "SINGLE_LIGHT",
      controllerPlacementType: [{ type: "NEAREST_DOOR_OUTSIDE_ROOMSET" }],
    },
  },
  {
    id: "9dJcWY4WLsxDovRy",
    filters: {
      roomType: [{ roomType: "FRONT_PORCH" }],
    },
    electricalLayout: {
      layoutType: "SINGLE_LIGHT",
      controllerPlacementType: [{ type: "NEAREST_DOOR_OUTSIDE_ROOMSET" }],
    },
  },
  {
    id: "GKgJaM7lTkWj2evH",
    filters: {
      roomType: [{ roomType: "REAR_PORCH" }],
      area: [{ minimum: 100 }],
    },
    electricalLayout: {
      layoutType: "FOUR_LIGHTS",
      addFan: true,
      controllerPlacementType: [{ type: "NEAREST_DOOR_OUTSIDE_ROOMSET" }],
    },
  },
  {
    id: "1WlwyCAjo3TtzUAt",
    filters: {
      roomType: [{ roomType: "POWDER" }],
    },
    electricalLayout: {
      layoutType: "SINGLE_LIGHT",
      controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
    },
  },
  {
    id: "WcMxFqm50dCjXewd",
    filters: {
      roomType: [{ roomType: "SHOWER" }],
    },
    electricalLayout: {
      layoutType: "SINGLE_LIGHT",
      controllerPlacementType: [
        {
          type: "NEAREST",
          candidateTypeConstraints: "BY_DOOR",
        },
      ],
    },
  },
  {
    id: "WIn10baYW0EZ6grI",
    filters: {
      roomType: [{ roomType: "WATER_CLOSET" }],
    },
    electricalLayout: {
      layoutType: "SINGLE_LIGHT",
      controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
    },
  },
  {
    id: "JINPzv9u9lu3tuHN",
    filters: {
      roomType: [{ roomType: "WATER_CLOSET" }, { roomType: "BATHFULL" }, { roomType: "POWDER" }],
    },
    electricalLayout: {
      layoutType: "SINGLE_LIGHT",
      loadType: "BATH_FAN",
      controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
    },
  },
  {
    id: "Vk7ONgPknZyKO4Cr",
    filters: {
      spaceType: [{ spaceType: "BEDROOM" }],
      roomType: [
        { roomType: "MASTER_BED", negate: true },
        { roomType: "BED_PASS", negate: true },
      ],
    },
    electricalLayout: {
      layoutType: "SINGLE_LIGHT",
      controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
    },
  },
  {
    id: "ysSDoMiR96q63mXG",
    filters: {
      spaceType: [{ spaceType: "CONTEMPLATIVE" }],
      roomType: [{ roomType: "FLEX", negate: true }],
    },
    electricalLayout: {
      layoutType: "SINGLE_LIGHT",
      controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
    },
  },

  {
    id: "npfrc8TFUBEW1Yeg",
    filters: {
      spaceType: [{ spaceType: "STORAGE" }],
      width: [{ minimum: 48 }],
    },
    electricalLayout: {
      layoutType: "SINGLE_LIGHT",
      controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
    },
  },
  {
    id: "jRkaYLYWoeRmEa3M",
    filters: {
      roomType: [{ roomType: "HALL" }],
    },
    electricalLayout: {
      layoutType: "SINGLE_LIGHT",
      controllerPlacementType: [{ type: "NEAREST" }],
    },
  },
  {
    id: "kqTeYEWUIvYq7oyI",
    filters: {
      roomType: [{ roomType: "LAUNDRY" }],
    },
    electricalLayout: {
      layoutType: "SINGLE_LIGHT",
      controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
    },
  },
  {
    id: "vCiWYRyHiwkD22N3",
    filters: {
      roomType: [{ roomType: "MASTER_VESTIBULE" }],
    },
    electricalLayout: {
      layoutType: "SINGLE_LIGHT",
      controllerPlacementType: [{ type: "PRIMARY_ENTRANCE" }],
    },
  },
  {
    id: "jk2l5aWcW4oFq8Yl",
    filters: {
      roomType: [{ roomType: "DINING" }],
    },
    electricalLayout: {
      layoutType: "SINGLE_LIGHT",
      controllerPlacementType: [{ type: "NEAREST" }],
    },
  },
  {
    id: "6JwekGWXhSEY71bV",
    filters: {
      roomType: [{ roomType: "LIVING" }],
    },
    electricalLayout: {
      layoutType: "FOUR_LIGHTS",
      addFan: true,
      controllerPlacementType: [
        {
          type: "NEAREST",
        },
      ],
    },
  },
  {
    id: "358HEPWjfgvBV30o",
    filters: {
      roomType: [{ roomType: "KITCHEN" }],
    },
    electricalLayout: {
      layoutType: "FOUR_LIGHTS",
      cornerInset: 42,
      addFan: false,
      controllerPlacementType: [
        {
          type: "NEAREST",
        },
      ],
    },
  },
];
